// src/components/atomic-crm/contacts/useContactImport.tsx
import { useDataProvider, useGetIdentity, type DataProvider } from "ra-core";
import { useCallback, useMemo } from "react";
import type { Company, Tag } from "../types";
import { supabase } from "../providers/supabase/supabase";
import { enrichAvatar } from "../providers/supabase/data-provider/enrichAvatar";


export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  company: string;
  email_work: string;
  email_home: string;
  email_other: string;
  phone_work: string;
  phone_home: string;
  phone_other: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  has_newsletter: string;
  status: string;
  tags: string;
  linkedin_url: string;
};

export function useContactImport() {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  const companiesCache = useMemo(
    () => new Map<string, Company>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );

  // 👇 Now accepts orgId as an explicit argument
  const getCompanies = useCallback(
    async (names: string[], orgId: string) =>
      fetchRecordsWithCache<Company>(
        "companies",
        companiesCache,
        names,
        (name) => ({
          name,
          created_at: new Date().toISOString(),
          sales_id: user?.identity?.id,
          org_id: orgId,
        }),
        dataProvider,
      ),
    [companiesCache, user?.identity?.id, dataProvider],
  );

  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);

  // 👇 Now accepts orgId as an explicit argument
  const getTags = useCallback(
    async (names: string[], orgId: string) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        (name) => ({
          name,
          color: "#f9f9f9",
          org_id: orgId,
        }),
        dataProvider,
      ),
    [tagsCache, dataProvider],
  );
  const processBatch = useCallback(
    async (batch: ContactImportSchema[]) => {
      // Fetch org_id synchronously at import time
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not authenticated");

      const { data: sale, error: saleError } = await supabase
        .from("sales")
        .select("org_id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (saleError) throw new Error(saleError.message);
      if (!sale?.org_id) {
        throw new Error(
          "Your account has no org_id — please complete workspace setup first.",
        );
      }

      const orgId: string = sale.org_id;
      console.log("[Import] orgId:", orgId);

      const [companies, tags] = await Promise.all([
        getCompanies(
          batch.map((c) => c.company?.trim()).filter((n): n is string => !!n),
          orgId,
        ),
        getTags(
          batch.flatMap((b) => parseTags(b.tags)),
          orgId,
        ),
      ]);

      await Promise.all(
        batch.map(
          async ({
            first_name,
            last_name,
            gender,
            title,
            email_work,
            email_home,
            email_other,
            phone_work,
            phone_home,
            phone_other,
            background,
            first_seen,
            last_seen,
            has_newsletter,
            status,
            company: companyName,
            tags: tagNames,
            linkedin_url,
          }) => {
            const email_jsonb = [
              { email: email_work, type: "Work" },
              { email: email_home, type: "Home" },
              { email: email_other, type: "Other" },
            ].filter(({ email }) => email);

            const phone_jsonb = [
              { number: phone_work, type: "Work" },
              { number: phone_home, type: "Home" },
              { number: phone_other, type: "Other" },
            ].filter(({ number }) => number);

            const company = companyName?.trim()
              ? companies.get(companyName.trim())
              : undefined;

            const tagList = parseTags(tagNames)
              .map((name) => tags.get(name))
              .filter((tag): tag is Tag => !!tag);

            // 👇 Capture the result so we can enrich it in the background
            const created = await dataProvider.create("contacts", {
              data: {
                first_name,
                last_name,
                gender,
                title,
                email_jsonb,
                phone_jsonb,
                background,
                first_seen: first_seen
                  ? new Date(first_seen).toISOString()
                  : today,
                last_seen: last_seen
                  ? new Date(last_seen).toISOString()
                  : today,
                has_newsletter,
                status,
                company_id: company?.id,
                tags: tagList.map((tag) => tag.id),
                sales_id: user?.identity?.id,
                linkedin_url,
                org_id: orgId,
              },
            });

            // 👇 Fire-and-forget: runs in the background, never blocks,
            // never throws into the caller. If avatar fetch fails, the
            // contact is already saved — we just skip the decoration.
            void enrichAvatar("contacts", created.data);

            return created;
          },
        ),
      );
    },
    [dataProvider, getCompanies, getTags, user?.identity?.id, today],
  );

  return processBatch;
}

const fetchRecordsWithCache = async function <T>(
  resource: string,
  cache: Map<string, T>,
  names: string[],
  getCreateData: (name: string) => Partial<T>,
  dataProvider: DataProvider,
) {
  const trimmedNames = [...new Set(names.map((name) => name.trim()))];
  const uncachedRecordNames = trimmedNames.filter((name) => !cache.has(name));

  if (uncachedRecordNames.length > 0) {
    const response = await dataProvider.getList(resource, {
      filter: {
        "name@in": `(${uncachedRecordNames
          .map((name) => `"${name}"`)
          .join(",")})`,
      },
      pagination: { page: 1, perPage: trimmedNames.length },
      sort: { field: "id", order: "ASC" },
    });
    for (const record of response.data) {
      cache.set(record.name.trim(), record);
    }
  }

  await Promise.all(
    uncachedRecordNames.map(async (name) => {
      if (cache.has(name)) return;
      const response = await dataProvider.create(resource, {
        data: getCreateData(name),
      });
      cache.set(name, response.data);

      // 👇 Fire-and-forget avatar enrichment for companies only
      // (contacts go through processBatch, tags don't need avatars)
      if (resource === "companies") {
        void enrichAvatar("companies", response.data);
      }
    }),
  );

  return trimmedNames.reduce((acc, name) => {
    acc.set(name, cache.get(name) as T);
    return acc;
  }, new Map<string, T>());
};

const parseTags = (tags: string) =>
  tags
    ?.split(",")
    ?.map((tag: string) => tag.trim())
    ?.filter((tag: string) => tag) ?? [];
